import { Box, BoxProps, Button, SizableText, SmartLink, TextLink, XStack, YStack } from '@my/ui'
import { GroupEvent, GroupEventRsvps, User } from 'app/types'
import { PostUser, GroupUser } from '..'
import { format } from 'date-fns'
import { MapPin, Share2 } from '@tamagui/lucide-icons'
import { SolitoImage } from 'solito/image'
import { GroupEventRsvpDropdown } from '../../containers/group-event-rsvp-dropdown'
import { useState } from 'react'
import { ShareModal } from 'app/features/sharing/components'
import config from 'app/config'
import { GroupEventOptions } from '../../containers/group-event-options'
import { GroupEventRsvpStats } from '../../containers'

const interactionStyles = {
  hoverStyle: {
    opacity: 0.9,
  },
  focusStyle: {
    opacity: 0.85,
  },
  pressStyle: {
    opacity: 0.8,
  },
}
export interface GroupEventCardProps {
  displayAs?: 'feedItem' | 'post'
  user: Pick<User, 'id' | 'fullName' | 'avatar'> | null
  canInteract: boolean
  event: GroupEvent
  rsvps?: GroupEventRsvps
  onAfterDelete?: () => void
}

export const GroupEventCard = ({
  user,
  canInteract,
  event,
  displayAs = 'post',
  onAfterDelete,
  ...props
}: GroupEventCardProps & BoxProps) => {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const formattedStartDate = format(new Date(event.startDate), 'EEE, MMM d')
  const formattedStartTime = format(new Date(event.startDate), 'h:mm b')
  const shouldExpandDescription = event.description?.length > 60
  const shortDescription = shouldExpandDescription
    ? event.description.slice(0, 60) + '...'
    : event.description
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)

  return (
    <Box ai="stretch" fd="column" gap="$3.5" {...props}>
      <XStack flex={1} jc="space-between" ai="flex-start">
        {displayAs === 'post' && (
          <PostUser creator={event.creator} group={event.group} createdAt={event.createdAt} />
        )}
        {displayAs === 'feedItem' && (
          <GroupUser creator={event.creator} group={event.group} createdAt={event.createdAt} />
        )}

        {canInteract && <GroupEventOptions event={event} onAfterDelete={onAfterDelete} />}
      </XStack>
      <YStack>
        <SmartLink tabIndex={-1} Component={YStack} href={event.href} {...interactionStyles}>
          {event.image?.url && (
            <YStack flexGrow={1} w="100%" aspectRatio={65 / 28} overflow="hidden" br="$3">
              <SolitoImage
                src={event.image.url}
                alt={event.image.alt || ''}
                contentFit="cover"
                fill
                sizes="(max-width: 768px) 100vw, 600px"
              />
            </YStack>
          )}
        </SmartLink>

        <YStack p="$3.5" bg="$overlay1">
          <SmartLink
            Component={XStack}
            href={event.href}
            tabIndex={-1}
            gap="$2"
            flex={1}
            pb="$3"
            {...interactionStyles}
          >
            <SizableText textTransform="uppercase" size="$1" color="$overlay9">
              {formattedStartDate} at {formattedStartTime}
            </SizableText>
            {event.location && (
              <XStack ai="center" gap="$1">
                <MapPin size={12} />
                <SizableText size="$1" color="$overlay9" numberOfLines={1}>
                  {event.location}
                </SizableText>
              </XStack>
            )}
          </SmartLink>

          <XStack gap="$2" jc="space-between" ai="flex-start" minHeight={60}>
            <YStack flex={1} gap="$2">
              <TextLink
                href={event.href}
                size="$4.5"
                fow="700"
                color="$overlay12"
                textDecorationLine="none"
                {...interactionStyles}
              >
                {event.name}
              </TextLink>

              <SizableText size="$2" color="$overlay12">
                {descriptionExpanded ? event.description : shortDescription}{' '}
                {shouldExpandDescription && (
                  <SizableText
                    tag="button"
                    onPress={() => setDescriptionExpanded(!descriptionExpanded)}
                    size="$2"
                    color="$overlay12"
                    fow="600"
                    cursor="pointer"
                    hoverStyle={{
                      opacity: 0.85,
                    }}
                    focusStyle={{
                      opacity: 0.85,
                    }}
                    pressStyle={{
                      opacity: 0.8,
                    }}
                  >
                    Show {descriptionExpanded ? 'less' : 'more'}
                  </SizableText>
                )}
              </SizableText>
            </YStack>

            <GroupEventRsvpStats eventId={event.eventId} />
          </XStack>

          {/* Event Actions */}
          <XStack w="100%" pt="$3.5" gap="$2">
            {user?.id && canInteract && (
              <GroupEventRsvpDropdown
                userId={user.id}
                eventId={event.eventId}
                open={dropdownOpen}
                onOpenChange={setDropdownOpen}
              />
            )}
            <ShareModal
              href={config.siteUrl + event.href}
              subject="Check out this event!"
              trigger={<Button ml="auto" size="$3" px="$2" plain icon={<Share2 size={24} />} />}
            />
          </XStack>
        </YStack>
      </YStack>
    </Box>
  )
}
